import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SvgIconComponent } from '@web/shared/components/svg-icon/svg-icon.component';
import { TooltipModule } from 'primeng/tooltip';
import {
  groupOwnsUrl,
  MultiSidebarGroup,
  MultiSidebarL1,
  MultiSidebarLink,
  MultiSidebarSection,
  pathMatchesLink,
  sectionOwnsUrl,
} from './multi-sidebar.types';

@Component({
  selector: 'app-multi-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, SvgIconComponent, TooltipModule],
  template: `
    <nav
      class="multi-sidebar flex flex-col h-full"
      [class.collapsed]="isIconMode()"
      [class.hover-open]="collapsed() && hoverExpanded()"
      aria-label="Main"
      (mouseenter)="onMenuEnter()"
      (mouseleave)="onMenuLeave()"
    >
      <div
        class="toolbar shrink-0 flex items-center"
        [class.justify-end]="!isIconMode()"
        [class.justify-center]="isIconMode()"
      >
        <button
          type="button"
          class="icon-btn"
          [pTooltip]="collapsed() ? expandLabel() : collapseLabel()"
          tooltipPosition="right"
          (click)="onToggleCollapsed($event)"
        >
          <i
            class="pi text-sm"
            [class.pi-angle-double-right]="collapsed()"
            [class.pi-angle-double-left]="!collapsed()"
          ></i>
        </button>
      </div>

      <div class="nav-body flex-1 min-h-0 overflow-y-auto px-1.5 pb-2 flex flex-col gap-1">
        @for (group of items(); track group.id) {
          <div class="l1-block" [class.l1-active]="isGroupActive(group)" [class.l1-closed]="!isGroupOpen(group.id)">
            <button
              type="button"
              class="l1-header"
              [class.active]="isGroupActive(group)"
              [class.open]="isGroupOpen(group.id)"
              [attr.aria-expanded]="isGroupOpen(group.id)"
              [pTooltip]="group.label"
              [tooltipDisabled]="!isIconMode()"
              tooltipPosition="right"
              (click)="toggleGroup(group.id)"
            >
              <ng-container *ngTemplateOutlet="iconTpl; context: { $implicit: group.icon, size: 'l1' }" />
              @if (!isIconMode()) {
                <span class="l1-label">{{ group.label }}</span>
                <i
                  class="pi l1-chevron ml-auto"
                  [class.pi-angle-down]="isGroupOpen(group.id)"
                  [class.pi-angle-right]="!isGroupOpen(group.id)"
                ></i>
              }
            </button>

            @if (isGroupOpen(group.id)) {
              <div class="l1-children" [class.icon-mode]="isIconMode()">
                @for (child of group.children; track child.id) {
                  @if (child.kind === 'section') {
                    <div class="l2-block" [class.l2-active]="isSectionActive(child)">
                      <button
                        type="button"
                        class="l2-header"
                        [class.active]="isSectionActive(child)"
                        [class.open]="isSectionOpen(child.id)"
                        [pTooltip]="child.label"
                        [tooltipDisabled]="!isIconMode()"
                        tooltipPosition="right"
                        (click)="toggleSection(child.id)"
                      >
                        <ng-container *ngTemplateOutlet="iconTpl; context: { $implicit: child.icon, size: 'l2' }" />
                        @if (!isIconMode()) {
                          <span class="l2-label">{{ child.label }}</span>
                          <i
                            class="pi chevron ml-auto text-[0.65rem] opacity-60"
                            [class.pi-chevron-down]="!isSectionOpen(child.id)"
                            [class.pi-chevron-up]="isSectionOpen(child.id)"
                          ></i>
                        }
                      </button>

                      @if (isSectionOpen(child.id)) {
                        <div class="l3-list">
                          @for (link of child.children; track link.id) {
                            <ng-container *ngTemplateOutlet="l3Link; context: { $implicit: link }" />
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    <ng-container *ngTemplateOutlet="l3Link; context: { $implicit: child }" />
                  }
                }
              </div>
            }
          </div>
        }
      </div>

      <div class="sidebar-footer" [class.collapsed-footer]="isIconMode()">
        <ng-content select="[multiSidebarFooter]" />
      </div>
    </nav>

    <ng-template #l3Link let-link>
      <a
        class="l3-item"
        [class.active]="isLinkActive(link)"
        [routerLink]="link.routerLink"
        [pTooltip]="link.label"
        [tooltipDisabled]="!isIconMode()"
        tooltipPosition="right"
      >
        <ng-container *ngTemplateOutlet="iconTpl; context: { $implicit: link.icon, size: 'l3' }" />
        @if (!isIconMode()) {
          <span class="l3-label">{{ link.label }}</span>
        }
      </a>
    </ng-template>

    <ng-template #iconTpl let-icon let-size="size">
      @if (icon.primeIcon) {
        <span
          class="nav-icon"
          [class]="icon.primeIcon + (size === 'l1' ? ' nav-icon-l1' : size === 'l2' ? ' nav-icon-l2' : ' nav-icon-l3')"
        ></span>
      } @else {
        <app-svg-icon
          class="nav-icon-svg"
          [style.width.rem]="size === 'l1' ? 1.1 : size === 'l2' ? 1 : 0.9"
          [style.height.rem]="size === 'l1' ? 1.1 : size === 'l2' ? 1 : 0.9"
          [icon]="icon.customIcon"
        />
      }
    </ng-template>
  `,
  styles: `
    :host {
      display: block;
      height: 100%;
      color: var(--color-white);
    }

    .multi-sidebar {
      width: 15.5rem;
      transition: width 180ms ease;
    }

    .multi-sidebar.collapsed {
      width: 3.75rem;
    }

    .multi-sidebar.hover-open {
      width: 15.5rem;
    }

    .toolbar {
      padding: 0.5rem 0.35rem 0.25rem;
    }

    .icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--color-white);
      cursor: pointer;
    }

    .icon-btn:hover {
      background: rgb(255 255 255 / 0.08);
    }

    .l1-block {
      padding-bottom: 0.35rem;
      margin-bottom: 0.15rem;
      border-bottom: 1px solid rgb(255 255 255 / 0.1);
    }

    .l1-block:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }

    .collapsed .l1-block {
      padding-bottom: 0.45rem;
      margin-bottom: 0.25rem;
      border-bottom-color: rgb(255 255 255 / 0.18);
    }

    .l1-header,
    .l2-header,
    .l3-item {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      width: 100%;
      border: none;
      border-radius: 0.375rem;
      color: var(--color-white);
      background: transparent;
      text-decoration: none;
      text-align: left;
      transition:
        background-color 160ms ease,
        color 160ms ease,
        box-shadow 160ms ease;
    }

    .l1-header {
      cursor: pointer;
      min-height: 2rem;
      padding: 0.3rem 0.5rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: rgb(255 255 255 / 0.55);
    }

    .l1-header:hover {
      background: rgb(255 255 255 / 0.06);
      color: rgb(255 255 255 / 0.78);
    }

    .l1-chevron {
      flex-shrink: 0;
      font-size: 0.55rem;
      opacity: 0.35;
      transition: opacity 120ms ease;
    }

    .l1-header:hover .l1-chevron {
      opacity: 0.65;
    }

    .l2-header,
    .l3-item {
      cursor: pointer;
    }

    .collapsed .l1-header,
    .collapsed .l2-header,
    .collapsed .l3-item {
      justify-content: center;
      gap: 0;
      padding-inline: 0;
      width: 2.6rem;
      margin-inline: auto;
    }

    .collapsed .l1-header {
      min-height: 2.35rem;
      border-radius: 0.5rem;
      background: rgb(255 255 255 / 0.07);
      color: var(--color-white);
      margin-bottom: 0.25rem;
    }

    .l1-header.active {
      color: var(--color-white);
    }

    .collapsed .l1-header.active,
    .collapsed .l1-block.l1-active .l1-header {
      background: rgb(255 255 255 / 0.14);
      box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.2);
    }

    .l2-header:hover,
    .l3-item:hover {
      background: rgb(255 255 255 / 0.08);
    }

    .l2-header.active {
      background: rgb(255 255 255 / 0.1);
      box-shadow: inset 2px 0 0 var(--color-primary);
    }

    .collapsed .l2-header {
      min-height: 2.1rem;
      border-radius: 0.45rem;
      background: rgb(255 255 255 / 0.04);
      margin-block: 0.1rem;
    }

    .collapsed .l2-header.active,
    .collapsed .l2-block.l2-active > .l2-header {
      background: rgb(255 255 255 / 0.12);
      box-shadow: inset 0 0 0 1px var(--color-primary);
    }

    .l3-item.active {
      background: var(--color-primary);
      color: var(--color-black);
      font-weight: 700;
    }

    .l3-item.active .nav-icon-svg {
      color: var(--color-black);
    }

    .collapsed .l3-item {
      min-height: 1.9rem;
      opacity: 0.92;
    }

    .collapsed .l3-item.active {
      opacity: 1;
      box-shadow: 0 0 0 1px rgb(255 255 255 / 0.25);
    }

    .l1-label {
      font-size: 0.75rem;
      line-height: 1.15;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .l1-children {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      padding: 0.1rem 0 0.15rem 0.25rem;
    }

    .l1-children.icon-mode {
      padding-left: 0;
      align-items: center;
      gap: 0.2rem;
    }

    .l2-header {
      min-height: 1.9rem;
      padding: 0.25rem 0.45rem;
      color: rgb(255 255 255 / 0.92);
    }

    .l2-label {
      font-size: 0.8125rem;
      font-weight: 600;
      line-height: 1.15;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .l3-list {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      padding: 0.1rem 0 0.2rem 0.55rem;
    }

    .collapsed .l3-list {
      padding: 0.15rem 0 0.25rem;
      align-items: center;
      border-left: none;
      gap: 0.15rem;
    }

    .l3-item {
      min-height: 1.75rem;
      padding: 0.2rem 0.45rem;
    }

    .l3-label {
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.15;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .chevron {
      flex-shrink: 0;
    }

    .nav-icon {
      line-height: 1;
      flex-shrink: 0;
    }

    .nav-icon-l1 {
      font-size: 1.1rem;
    }

    .nav-icon-l2 {
      font-size: 1rem;
    }

    .nav-icon-l3 {
      font-size: 0.9rem;
    }

    .nav-icon-svg {
      display: block;
      flex-shrink: 0;
      color: inherit;
    }

    .sidebar-footer {
      flex-shrink: 0;
      border-top: 1px solid rgb(255 255 255 / 0.1);
      padding: 0.35rem 0.5rem 0.45rem;
    }

    .collapsed-footer {
      padding-inline: 0.25rem;
    }

    .collapsed-footer ::ng-deep .build-info {
      flex-direction: column;
      gap: 0.15rem;
      white-space: normal;
      text-align: center;
      font-size: 0.55rem;
    }

    .hover-open .collapsed-footer ::ng-deep .build-info {
      flex-direction: row;
      flex-wrap: wrap;
      font-size: 0.65rem;
      white-space: nowrap;
    }
  `,
})
export class MultiSidebarComponent {
  readonly items = input.required<readonly MultiSidebarL1[]>();
  readonly currentUrl = input.required<string>();
  readonly collapsed = input(false);
  readonly expandLabel = input('Expand');
  readonly collapseLabel = input('Collapse');

  readonly collapsedChange = output<boolean>();

  readonly hoverExpanded = signal(false);

  /** Closed L1 ids — empty means all open (default). */
  private readonly l1Closed = signal<ReadonlySet<string>>(readL1ClosedPreference());
  private readonly sectionOpenOverrides = signal<ReadonlyMap<string, boolean>>(new Map());

  readonly isIconMode = computed(() => this.collapsed() && !this.hoverExpanded());

  private readonly routeOpenSections = computed(() => {
    const url = this.currentUrl();
    const open = new Set<string>();
    for (const group of this.items()) {
      for (const child of group.children) {
        if (child.kind === 'section' && sectionOwnsUrl(child, url)) {
          open.add(child.id);
        }
      }
    }
    return open;
  });

  /** While hover-expanding a pinned-collapsed menu, open all L2 sections. */
  private readonly hoverForceSections = computed(() => {
    if (!this.collapsed() || !this.hoverExpanded()) {
      return null;
    }
    const open = new Set<string>();
    for (const group of this.items()) {
      for (const child of group.children) {
        if (child.kind === 'section') {
          open.add(child.id);
        }
      }
    }
    return open;
  });

  constructor() {
    effect(() => {
      this.currentUrl();
      this.sectionOpenOverrides.set(new Map());
    });

    // Active route wins: reopen its L1 and persist.
    effect(() => {
      const url = this.currentUrl();
      const closed = new Set(this.l1Closed());
      let changed = false;
      for (const group of this.items()) {
        if (groupOwnsUrl(group, url) && closed.has(group.id)) {
          closed.delete(group.id);
          changed = true;
        }
      }
      if (changed) {
        this.l1Closed.set(closed);
        writeL1ClosedPreference(closed);
      }
    });
  }

  isGroupOpen(id: string): boolean {
    return !this.l1Closed().has(id);
  }

  toggleGroup(id: string): void {
    const closed = new Set(this.l1Closed());
    if (closed.has(id)) {
      closed.delete(id);
    } else {
      closed.add(id);
    }
    this.l1Closed.set(closed);
    writeL1ClosedPreference(closed);
  }

  isSectionOpen(id: string): boolean {
    const hoverForce = this.hoverForceSections();
    if (hoverForce?.has(id)) {
      return true;
    }
    const override = this.sectionOpenOverrides().get(id);
    if (override !== undefined) {
      return override;
    }
    return this.routeOpenSections().has(id);
  }

  toggleSection(id: string): void {
    const next = !this.isSectionOpen(id);
    const map = new Map(this.sectionOpenOverrides());
    map.set(id, next);
    this.sectionOpenOverrides.set(map);
  }

  onMenuEnter(): void {
    if (this.collapsed()) {
      this.hoverExpanded.set(true);
    }
  }

  onMenuLeave(): void {
    this.hoverExpanded.set(false);
  }

  onToggleCollapsed(event: MouseEvent): void {
    event.stopPropagation();
    this.hoverExpanded.set(false);
    this.collapsedChange.emit(!this.collapsed());
  }

  isLinkActive(link: MultiSidebarLink): boolean {
    return pathMatchesLink(this.currentUrl(), link);
  }

  isGroupActive(group: MultiSidebarGroup): boolean {
    return groupOwnsUrl(group, this.currentUrl());
  }

  isSectionActive(section: MultiSidebarSection): boolean {
    return sectionOwnsUrl(section, this.currentUrl());
  }
}

const L1_CLOSED_STORAGE_KEY = 'soe.mainSidebar.l1Closed';

function readL1ClosedPreference(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(L1_CLOSED_STORAGE_KEY);
    if (raw === null || raw === '') {
      return new Set();
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function writeL1ClosedPreference(closed: ReadonlySet<string>): void {
  try {
    localStorage.setItem(L1_CLOSED_STORAGE_KEY, JSON.stringify([...closed]));
  } catch {
    // ignore
  }
}
